/**
 * SPEC-006 §61/§62/EDGE-REQ-039/T051 — structural edge validation applied
 * when a graph is loaded. Dangling/self-loop/duplicate-ID edges are never
 * silently destroyed: they are excluded from the active, connection-
 * bearing edge list and reported as warnings, while the caller keeps the
 * original raw graph available for recovery/analysis.
 */
export interface RawEdgeLike {
  id: string;
  source: string;
  target: string;
}

export type EdgeIssueReason = 'dangling-source' | 'dangling-target' | 'self-loop' | 'duplicate-id';

export interface EdgeValidationIssue {
  edgeId: string;
  reason: EdgeIssueReason;
}

export interface GraphEdgeValidationResult<T extends RawEdgeLike> {
  validEdges: T[];
  invalidEdges: T[];
  warnings: EdgeValidationIssue[];
}

export function validateGraphEdges<T extends RawEdgeLike>(
  nodeIds: ReadonlySet<string>,
  edges: readonly T[],
): GraphEdgeValidationResult<T> {
  const validEdges: T[] = [];
  const invalidEdges: T[] = [];
  const warnings: EdgeValidationIssue[] = [];
  const seenIds = new Set<string>();

  for (const edge of edges) {
    if (seenIds.has(edge.id)) {
      invalidEdges.push(edge);
      warnings.push({ edgeId: edge.id, reason: 'duplicate-id' });
      continue;
    }
    seenIds.add(edge.id);

    if (edge.source === edge.target) {
      invalidEdges.push(edge);
      warnings.push({ edgeId: edge.id, reason: 'self-loop' });
      continue;
    }
    if (!nodeIds.has(edge.source)) {
      invalidEdges.push(edge);
      warnings.push({ edgeId: edge.id, reason: 'dangling-source' });
      continue;
    }
    if (!nodeIds.has(edge.target)) {
      invalidEdges.push(edge);
      warnings.push({ edgeId: edge.id, reason: 'dangling-target' });
      continue;
    }

    validEdges.push(edge);
  }

  return { validEdges, invalidEdges, warnings };
}
